import React from 'react';
import { useSite } from '../../context/SiteContext';
import { motion } from 'framer-motion';

export const ContentSection = () => {
  const { settings, contentSections } = useSite();

  return (
    <main className="mx-auto max-w-7xl px-4 pb-16">
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* What Is Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl border border-slate-200 bg-white shadow-lg p-7 md:p-10"
            id="what-is"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
              {contentSections?.what_is?.title || 'What This Product Is Designed For'}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {contentSections?.what_is?.content ||
                'This supplement is positioned as a daily option for those who want a simpler routine and consistent support.'}
            </p>

            <div className="mt-7 rounded-2xl bg-slate-50 border border-slate-200 p-6">
              <div className="font-heading font-bold">Quick expectations (keep it real)</div>
              <ul className="mt-3 space-y-2 text-slate-700">
                <li className="flex gap-2">
                  <span className="mt-2 h-2 w-2 rounded-full bg-slate-900 flex-shrink-0" />
                  Results vary by person and routine.
                </li>
                <li className="flex gap-2">
                  <span className="mt-2 h-2 w-2 rounded-full bg-slate-900 flex-shrink-0" />
                  Consistency matters more than "one-time use".
                </li>
                <li className="flex gap-2">
                  <span className="mt-2 h-2 w-2 rounded-full bg-slate-900 flex-shrink-0" />
                  Better outcomes often pair with sleep, training, hydration, and nutrition.
                </li>
              </ul>
            </div>

            <h3 className="mt-10 font-heading text-xl md:text-2xl font-bold">
              How People Typically Use It
            </h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Most users follow the label directions and stick with the same timing daily. Many also
              track how they feel week-by-week rather than judging after a single day. This is why
              multi-bottle bundles are commonly chosen: they support consistency without
              interruptions.
            </p>

            <h3 className="mt-10 font-heading text-xl md:text-2xl font-bold">
              Why Bundles Save More
            </h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              The pricing structure rewards longer plans. The 6-bottle package shows the best
              per-bottle price and includes extra bonuses, while 3 bottles offers a balanced
              mid-range option with free US shipping. If you're just testing, 1 bottle is available,
              but it usually has the highest cost per bottle.
            </p>

            <div className="mt-10 rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-heading font-bold text-lg">Recommended picks</div>
                  <div className="text-sm text-slate-600">
                    Choose based on your timeline and goal.
                  </div>
                </div>
                <a
                  href="#order"
                  className="rounded-full px-5 py-2 font-bold btn-cta shadow-lg"
                  data-testid="see-packages-btn"
                >
                  See Packages
                </a>
              </div>

              <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="font-heading font-bold">Try it</div>
                  <div className="mt-1 text-slate-600">Go with 1 bottle if you're only testing.</div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="font-heading font-bold">Stay consistent</div>
                  <div className="mt-1 text-slate-600">3 bottles for a 90-day rhythm.</div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="font-heading font-bold">Best value</div>
                  <div className="mt-1 text-slate-600">6 bottles for maximum savings + bonuses.</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Ingredients Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl border border-slate-200 bg-white shadow-lg p-7 md:p-10"
            id="ingredients"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
              {contentSections?.ingredients?.title || 'Ingredients'}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {contentSections?.ingredients?.content ||
                'Ingredient lists can change by batch and region. Always refer to the label on the bottle you receive as the most accurate source.'}
            </p>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="font-heading font-bold">What to look for on the label</div>
                <ul className="mt-3 space-y-2 text-slate-700 text-sm">
                  <li className="flex gap-2">
                    <span className="mt-2 h-2 w-2 rounded-full bg-slate-900 flex-shrink-0" />
                    Serving size and daily directions
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-2 h-2 w-2 rounded-full bg-slate-900 flex-shrink-0" />
                    Allergen warnings
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-2 h-2 w-2 rounded-full bg-slate-900 flex-shrink-0" />
                    Storage instructions
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-2 h-2 w-2 rounded-full bg-slate-900 flex-shrink-0" />
                    Expiration and batch info
                  </li>
                </ul>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="font-heading font-bold">Quality checklist</div>
                <ul className="mt-3 space-y-2 text-slate-700 text-sm">
                  <li className="flex gap-2">
                    <span className="mt-2 h-2 w-2 rounded-full bg-slate-900 flex-shrink-0" />
                    Buy from the official offer to avoid counterfeits
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-2 h-2 w-2 rounded-full bg-slate-900 flex-shrink-0" />
                    Don't rely on marketplace resellers
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-2 h-2 w-2 rounded-full bg-slate-900 flex-shrink-0" />
                    Keep screenshots/receipts for guarantee support
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-6">
            {/* Quick Order Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl border-2 border-green-500 bg-gradient-to-b from-green-50 to-white p-6 shadow-xl"
            >
              <div className="text-center">
                <div className="font-heading text-xl font-bold text-green-700">Best Deal</div>
                <div className="mt-2 font-heading text-4xl font-black" style={{ color: settings?.accent_color }}>
                  $49
                </div>
                <div className="text-sm text-slate-600">/bottle with 6-pack</div>
                <a
                  href="#order"
                  className="mt-4 block rounded-2xl btn-cta font-bold py-4 text-center shadow-lg"
                  data-testid="sidebar-order-btn"
                >
                  ORDER NOW
                </a>
                <div className="mt-3 text-xs text-slate-500">Free US Shipping • 60-Day Guarantee</div>
              </div>
            </motion.div>

            {/* Trust Elements */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-lg">
              <div className="font-heading font-bold text-sm">Why people choose us:</div>
              <ul className="mt-3 space-y-3 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center text-green-600">✓</span>
                  Official website only
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center text-green-600">✓</span>
                  60-day money back guarantee
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center text-green-600">✓</span>
                  Secure encrypted checkout
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center text-green-600">✓</span>
                  Fast shipping available
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContentSection;
